import { gl } from '../../../Engine';

/**
 * Common abstraction class for all Uniform variables of different types.
 * This class makes it easier to initialize and utilize the uniform variables introduced in WebGL.
 *
 * Uniform variables offer an interface to conveniently transfer variable data to WebGL Shader programs.
 *
 * The values stored in Uniform variables may be set/modified
 * before each gl.drawArrays or gl.drawElements -call.
 * Uniform variables will stay constant during the render call for every vertex and fragment.
 *
 * Uniform variable's may be introduced in both VERTEX_SHADER and FRAGMENT_SHADER stage using the syntax:
 * uniform mat4 transformation;
 * in which 'uniform' declares the variable as uniform, 'mat4' defines the type of the data and
 * 'transformation' is the variable name.
 *
 * In order to assign value to Uniform variable, it has to be bound to Shader.
 * This is done using {@link ShaderProgram.storeUniformLocations() ShaderProgram.storeUniformLocations(...uniforms: Uniform[])}.
 * Once the Uniform variable has been linked to shader,
 * we may start the shader program and assign value to the uniform variable using the load-methods it provides.
 */
export abstract class Uniform {
    /**
     * Name of the Uniform variable within the shader code.
     */
    protected readonly name: string;

    /**
     * WebGL link to that uniform variable.
     */
    protected location?: WebGLUniformLocation;

    /**
     * Constructor that requires the variable's name as a parameter.
     * @param name The name of the Uniform variable within the shader code.
     */
    constructor(name: string) {
        this.name = name;
    }

    /**
     * Method to link the uniform variable to the shader program.
     * This method should be called by {@link ShaderProgram.storeUniformLocations ShaderProgram.storeUniformLocations(...uniforms: Uniform[])}.
     *
     * @throws Error if the uniform binding fails due to {@link name name} mismatch.
     * @param programID Reference to the underlying WebGL {@link WebGLProgram ShaderProgram},
     * that should have UniformVariable identified by {@link name name}.
     * @returns True to signify that the uniform binding was successful
     */
    public storeUniformLocation(programID: WebGLProgram): boolean {
        const location =
            gl.getUniformLocation(programID, this.name) || undefined;
        if (!location) {
            throw new Error(
                "Attempted to load uniform location '" +
                    this.name +
                    "' that does not exist"
            );
        }
        this.location = location;
        return true;
    }
}
